import type { ReactElement } from "react";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import { STAGING_DIVERSION_TARGETS } from "./SystemSettingsStagingDiversion.constants";
import type { SystemSettingsStagingDiversionProps } from "./SystemSettingsStagingDiversion.types";
import { isBaseLocationValid } from "./SystemSettingsStagingDiversion.utils";

function FieldHeader({
  label,
  description,
  checkbox,
}: {
  label: string;
  description: string;
  checkbox: ReactElement;
}) {
  return (
    <div className="flex flex-col">
      <FormControlLabel control={checkbox} label={label} />
      <p className="m-0 pl-8 text-sm text-text-secondary">{description}</p>
    </div>
  );
}

export default function SystemSettingsStagingDiversion({
  value,
  onChange,
  scheme,
}: SystemSettingsStagingDiversionProps) {
  const baseInvalid = !isBaseLocationValid(value.base_location, scheme);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="text-sm font-medium">Output diversion</div>
        <p className="m-0 text-sm text-text-secondary">
          Base location and at least one target required.
        </p>
      </div>
      <FieldHeader
        label="Base location"
        description="Where everything a staging run writes goes."
        checkbox={<Checkbox size="small" checked disabled />}
      />
      <div className="pl-8">
        <TextField
          placeholder={`${scheme}my-tests-bucket or my-tests-base-dir`}
          value={value.base_location}
          onChange={(e) => onChange({ base_location: e.target.value })}
          error={baseInvalid}
          helperText={
            baseInvalid
              ? `Must start with ${scheme} or be a partial path`
              : undefined
          }
          fullWidth
          required
          size="small"
        />
      </div>
      {STAGING_DIVERSION_TARGETS.map((target) => {
        const current = value[target.key];
        const checked = current !== null;
        return (
          <div key={target.key} className="flex flex-col gap-2">
            <FieldHeader
              label={target.label}
              description={target.description}
              checkbox={
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={(e) =>
                    onChange({ [target.key]: e.target.checked ? "" : null })
                  }
                />
              }
            />
            {checked && (
              <div className="pl-8">
                <TextField
                  placeholder={target.placeholder}
                  value={current}
                  onChange={(e) => onChange({ [target.key]: e.target.value })}
                  fullWidth
                  size="small"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
